import {
  Control,
  Controller,
  FieldErrors,
  UseFormClearErrors,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import { useRef, useState } from 'react';
import { Editor } from 'tinymce';
import MergeTagOnlyEditor from '../../../../../shared/editor/mergetag-only-editor';
import DistractionFreeEditor from '../../../../../shared/editor/distraction-free-editor';
import SharedAutocomplete from '../../../../../shared/shared-autocomplete';
import AvatarChip from '../../../../../shared/avatar-chip';
import { ActionErrorEntry } from '../decom-policy-utils.tsx';
import { DecomPolicyFormValues } from '../decom-policy-form-layout.tsx';
import { clearFieldError } from './decom-action.tsx';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

interface SendEmailStepProps {
  control: Control<DecomPolicyFormValues>;
  watch: UseFormWatch<DecomPolicyFormValues>;
  setValue: UseFormSetValue<DecomPolicyFormValues>;
  clearErrors: UseFormClearErrors<DecomPolicyFormValues>;
  errors: FieldErrors<DecomPolicyFormValues>;
  trigger: UseFormTrigger<DecomPolicyFormValues>;
  setActionTypesWithErrors: React.Dispatch<
    React.SetStateAction<ActionErrorEntry[]>
  >;
  day: number;
}

export default function SendEmailStep({
  control,
  watch,
  setValue,
  errors,
  clearErrors,
  trigger,
  setActionTypesWithErrors,
  day,
}: SendEmailStepProps) {
  const actionsData = watch(`actions.${day}.actions`) || [];
  const idx = actionsData
    .map((action) => action.actionType)
    .indexOf('sendEmail');
  const htmlBody = watch(`actions.${day}.actions.${idx}.emailContent`) || '';
  const emailSubject =
    watch(`actions.${day}.actions.${idx}.emailSubject`) || '';
  const editorRef = useRef<Editor | null>(null);
  const editorSubjectRef = useRef<Editor | null>(null);
  const sendTo = watch(`actions.${day}.actions.${idx}.emailSendTo`) || [];
  const targetEmailTypes =
    watch(`actions.${day}.actions.${idx}.targetEmailType`) || [];
  const customEmailTypes =
    watch(`actions.${day}.actions.${idx}.customEmailType`) || [];

  const ccTo = watch(`actions.${day}.actions.${idx}.emailCcTo`) || [];
  const bccTo = watch(`actions.${day}.actions.${idx}.emailBccTo`) || [];
  const autocompleteKeySendTo = useRef(new Date().getTime().toString());
  const [autoCompleteResponseSendTo, setAutoCompleteResponseSendTo] =
    useState('');
  const autocompleteKeyCcTo = useRef(new Date().getTime().toString());
  const [autoCompleteResponseCcTo, setAutoCompleteResponseCcTo] = useState('');
  const autocompleteKeyBccTo = useRef(new Date().getTime().toString());
  const [autoCompleteResponseBccTo, setAutoCompleteResponseBccTo] =
    useState('');
  const [
    showAdditonalConfigurationOptions,
    setShowAdditionalConfigurationOptions,
  ] = useState(false);

  const [selectedEmailType, setSelectedEmailType] = useState<string>('');
  const [customEmailTypeValue, setCustomEmailTypeValue] = useState<string>('');

  const generalFieldMergeTags = {
    title: 'General Fields',
    menu: [
      {
        value: 'Relation-manager',
        title: 'Manager Relation',
      },
    ],
  };

  const emailTypes = [
    { label: 'Home', value: 'home' },
    { label: 'Work', value: 'work' },
    { label: 'Other', value: 'other' },
    { label: 'Custom', value: 'custom' },
  ];

  function setHtmlBody() {
    if (editorRef.current) {
      setValue(
        `actions.${day}.actions.${idx}.emailContent`,
        editorRef.current.getContent(),
        {
          shouldDirty: true,
        }
      );
    }
  }

  function setSubject() {
    if (editorSubjectRef.current) {
      setValue(
        `actions.${day}.actions.${idx}.emailSubject`,
        editorSubjectRef.current.getContent({ format: 'text' }),
        {
          shouldDirty: true,
        }
      );
    }
  }

  const handleAddEmailType = () => {
    if (!selectedEmailType) return;

    if (selectedEmailType === 'custom') {
      const trimmedCustomType = customEmailTypeValue.trim();
      if (!trimmedCustomType) return;

      if (
        customEmailTypes.some(
          (t) => t.toLowerCase() === trimmedCustomType.toLowerCase()
        )
      ) {
        setCustomEmailTypeValue('');
        return;
      }

      const newTargetTypes = targetEmailTypes.includes('custom')
        ? targetEmailTypes
        : [...targetEmailTypes, 'custom'];
      const newCustomTypes = [...customEmailTypes, trimmedCustomType];

      setValue(
        `actions.${day}.actions.${idx}.targetEmailType`,
        newTargetTypes,
        { shouldDirty: true }
      );
      setValue(
        `actions.${day}.actions.${idx}.customEmailType`,
        newCustomTypes,
        { shouldDirty: true }
      );
      setCustomEmailTypeValue('');
    } else {
      if (!targetEmailTypes.includes(selectedEmailType)) {
        setValue(
          `actions.${day}.actions.${idx}.targetEmailType`,
          [...targetEmailTypes, selectedEmailType],
          { shouldDirty: true }
        );
      }
    }

    clearFieldError('sendEmail', 'emailSendTo', setActionTypesWithErrors);
    clearErrors(`actions.${day}.actions.${idx}.emailSendTo`);
    clearFieldError('sendEmail', 'customEmailType', setActionTypesWithErrors);

    setSelectedEmailType('');
  };

  const handleDeleteEmailType = (type: string, isCustom: boolean) => {
    if (isCustom) {
      const newCustomTypes = customEmailTypes.filter((t) => t !== type);
      setValue(
        `actions.${day}.actions.${idx}.customEmailType`,
        newCustomTypes,
        { shouldDirty: true }
      );

      if (newCustomTypes.length === 0) {
        setValue(
          `actions.${day}.actions.${idx}.targetEmailType`,
          targetEmailTypes.filter((t) => t !== 'custom'),
          { shouldDirty: true }
        );
      }
    } else {
      setValue(
        `actions.${day}.actions.${idx}.targetEmailType`,
        targetEmailTypes.filter((t) => t !== type),
        { shouldDirty: true }
      );
    }
  };

  const displayChips = [
    ...targetEmailTypes
      .filter((t) => t !== 'custom')
      .map((t) => ({
        label: t.charAt(0).toUpperCase() + t.slice(1),
        value: t,
        isCustom: false,
      })),
    ...customEmailTypes.map((t) => ({
      label: `${t}`,
      value: t,
      isCustom: true,
    })),
  ];

  return (
    <Stack
      sx={{
        viewTransitionName: 'steps',
        p: 1,
      }}
      gap={1}
      width={'100%'}
    >
      <Stack>
        <Typography sx={{ fontWeight: 'bold' }}>To</Typography>
        <Controller
          name={`actions.${day}.actions.${idx}.useManagerEmail`}
          control={control}
          render={({ field: props }) => (
            <FormControlLabel
              control={
                <Checkbox
                  {...props}
                  checked={props.value as boolean}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    props.onChange(checked);

                    if (checked) {
                      clearFieldError(
                        'sendEmail',
                        'useManagerEmail',
                        setActionTypesWithErrors
                      );
                      clearFieldError(
                        'sendEmail',
                        'emailSendTo',
                        setActionTypesWithErrors
                      );
                      clearErrors(`actions.${day}.actions.${idx}.emailSendTo`);
                    } else {
                      if (
                        (!sendTo || sendTo.length === 0) &&
                        targetEmailTypes.length === 0
                      ) {
                        trigger(`actions.${day}.actions.${idx}.emailSendTo`, {
                          shouldFocus: true,
                        });
                      }
                    }
                  }}
                />
              }
              label="Include Manager"
            />
          )}
        />

        <Stack gap={1}>
          <Typography fontSize={'14px'} mt={2} mb={1}>
            {' '}
            Select one or more email types from the user's profile. If multiple
            types are selected, the system will attempt to send to the first
            available match based on your selection order.
          </Typography>
          <Stack sx={{ pb: 1, display: 'flex', flexDirection: 'row' }} gap={1}>
            <TextField
              select
              label="Email Type"
              size="small"
              value={selectedEmailType}
              onChange={(e) => setSelectedEmailType(e.target.value)}
              sx={{ width: 400 }}
            >
              {emailTypes.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            {selectedEmailType === 'custom' && (
              <TextField
                label="Custom Type"
                size="small"
                value={customEmailTypeValue}
                onChange={(e) => setCustomEmailTypeValue(e.target.value)}
                sx={{ width: 200 }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddEmailType();
                  }
                }}
              />
            )}
            <Button
              variant="contained"
              onClick={handleAddEmailType}
              disabled={
                !selectedEmailType ||
                (selectedEmailType === 'custom' && !customEmailTypeValue.trim())
              }
            >
              Add
            </Button>
          </Stack>
        </Stack>

        <Stack
          flexDirection="row"
          gap={1}
          flexWrap="wrap"
          sx={{ minHeight: 25 }}
        >
          {displayChips.map((chip, i) => (
            <AvatarChip
              key={`${chip.value}-${i}`}
              label={chip.label}
              type={'User'}
              email={chip.label}
              onDelete={() => handleDeleteEmailType(chip.value, chip.isCustom)}
            />
          ))}
        </Stack>

        <Stack flexDirection={'row'} gap={1} mt={1}>
          <Controller
            name={`actions.${day}.actions.${idx}.emailSendTo`}
            control={control}
            render={() => (
              <Box sx={{ width: 400 }}>
                <SharedAutocomplete
                  sources={['users', 'groups']}
                  autocompleteValue={autoCompleteResponseSendTo}
                  setAutoCompleteValue={(newValue) =>
                    setAutoCompleteResponseSendTo(newValue)
                  }
                  errors={!!errors?.actions?.[day]?.actions?.[idx]?.emailSendTo}
                  helperText={
                    errors?.actions?.[day]?.actions?.[idx]?.emailSendTo
                      ? errors?.actions?.[day]?.actions?.[idx]?.emailSendTo
                          ?.message
                      : ' '
                  }
                />
              </Box>
            )}
          />
          <Button
            sx={{ height: 40 }}
            variant="contained"
            onClick={() => {
              if ((sendTo || []).includes(autoCompleteResponseSendTo)) {
                autocompleteKeySendTo.current = new Date().getTime().toString();
                setAutoCompleteResponseSendTo('');
                return;
              }
              if (sendTo) {
                setValue(
                  `actions.${day}.actions.${idx}.emailSendTo`,
                  [...sendTo!, autoCompleteResponseSendTo],
                  { shouldDirty: true }
                );
              } else {
                setValue(
                  `actions.${day}.actions.${idx}.emailSendTo`,
                  [autoCompleteResponseSendTo],
                  { shouldDirty: true }
                );
              }
              clearFieldError(
                'sendEmail',
                'emailSendTo',
                setActionTypesWithErrors
              );
              clearFieldError(
                'sendEmail',
                'useManagerEmail',
                setActionTypesWithErrors
              );
              trigger(`actions.${day}.actions.${idx}.emailSendTo`);
              autocompleteKeySendTo.current = new Date().getTime().toString();
              setAutoCompleteResponseSendTo('');
            }}
            disabled={autoCompleteResponseSendTo.length === 0}
          >
            Add
          </Button>
        </Stack>
        {sendTo.length > 0 ? (
          <Stack flexDirection="row" gap={1} flexWrap="wrap">
            {sendTo.length > 0
              ? sendTo.map((email) => {
                  return (
                    <AvatarChip
                      key={email}
                      label={email}
                      type={'Unknown'}
                      email={email}
                      onDelete={() => {
                        setValue(
                          `actions.${day}.actions.${idx}.emailSendTo`,
                          sendTo.filter(
                            (currentEmail) => currentEmail !== email
                          ),
                          { shouldDirty: true }
                        );
                      }}
                    />
                  );
                })
              : null}
          </Stack>
        ) : null}
      </Stack>

      {showAdditonalConfigurationOptions ? (
        <Stack>
          <Stack sx={{ pb: 2 }} gap={1}>
            <Typography sx={{ fontWeight: 'bold' }}>Cc</Typography>
            <Stack flexDirection={'row'} gap={1}>
              <Controller
                name={`actions.${day}.actions.${idx}.emailCcTo`}
                control={control}
                render={() => (
                  <Box sx={{ width: 400 }}>
                    <SharedAutocomplete
                      sources={['users', 'groups']}
                      autocompleteValue={autoCompleteResponseCcTo}
                      setAutoCompleteValue={(newValue) =>
                        setAutoCompleteResponseCcTo(newValue)
                      }
                    />
                  </Box>
                )}
              />
              <Button
                sx={{ height: 40 }}
                variant="contained"
                onClick={() => {
                  if ((ccTo || []).includes(autoCompleteResponseCcTo)) {
                    autocompleteKeyCcTo.current = new Date()
                      .getTime()
                      .toString();
                    setAutoCompleteResponseCcTo('');
                    return;
                  }
                  if (ccTo) {
                    setValue(
                      `actions.${day}.actions.${idx}.emailCcTo`,
                      [...ccTo!, autoCompleteResponseCcTo],
                      { shouldDirty: true }
                    );
                  } else {
                    setValue(
                      `actions.${day}.actions.${idx}.emailCcTo`,
                      [autoCompleteResponseCcTo],
                      { shouldDirty: true }
                    );
                  }
                  autocompleteKeyCcTo.current = new Date().getTime().toString();
                  setAutoCompleteResponseCcTo('');
                }}
                disabled={autoCompleteResponseCcTo.length === 0}
              >
                Add
              </Button>
            </Stack>
            {ccTo.length > 0 ? (
              <Stack flexDirection="row" gap={1} flexWrap="wrap">
                {ccTo.length > 0
                  ? ccTo.map((email) => {
                      return (
                        <AvatarChip
                          key={email}
                          label={email}
                          type={'Unknown'}
                          email={email}
                          onDelete={() => {
                            setValue(
                              `actions.${day}.actions.${idx}.emailCcTo`,
                              ccTo.filter(
                                (currentEmail) => currentEmail !== email
                              ),
                              { shouldDirty: true }
                            );
                          }}
                        />
                      );
                    })
                  : null}
              </Stack>
            ) : null}
          </Stack>
          <Stack sx={{ pb: 2 }} gap={1}>
            <Typography sx={{ fontWeight: 'bold' }}>Bcc</Typography>
            <Stack flexDirection={'row'} gap={1}>
              <Controller
                name={`actions.${day}.actions.${idx}.emailBccTo`}
                control={control}
                render={() => (
                  <Box sx={{ width: 400 }}>
                    <SharedAutocomplete
                      sources={['users', 'groups']}
                      autocompleteValue={autoCompleteResponseBccTo}
                      setAutoCompleteValue={(newValue) =>
                        setAutoCompleteResponseBccTo(newValue)
                      }
                    />
                  </Box>
                )}
              />
              <Button
                sx={{ height: 40 }}
                variant="contained"
                onClick={() => {
                  if ((bccTo || []).includes(autoCompleteResponseBccTo)) {
                    autocompleteKeyBccTo.current = new Date()
                      .getTime()
                      .toString();
                    setAutoCompleteResponseBccTo('');
                    return;
                  }
                  if (bccTo) {
                    setValue(
                      `actions.${day}.actions.${idx}.emailBccTo`,
                      [...bccTo!, autoCompleteResponseBccTo],
                      { shouldDirty: true }
                    );
                  } else {
                    setValue(
                      `actions.${day}.actions.${idx}.emailBccTo`,
                      [autoCompleteResponseBccTo],
                      { shouldDirty: true }
                    );
                  }
                  autocompleteKeyBccTo.current = new Date()
                    .getTime()
                    .toString();
                  setAutoCompleteResponseBccTo('');
                }}
                disabled={autoCompleteResponseBccTo.length === 0}
              >
                Add
              </Button>
            </Stack>
            {bccTo.length > 0 ? (
              <Stack flexDirection="row" gap={1} flexWrap="wrap">
                {bccTo.length > 0
                  ? bccTo.map((email) => {
                      return (
                        <AvatarChip
                          key={email}
                          label={email}
                          type={'Unknown'}
                          email={email}
                          onDelete={() => {
                            setValue(
                              `actions.${day}.actions.${idx}.emailBccTo`,
                              bccTo.filter(
                                (currentEmail) => currentEmail !== email
                              ),
                              { shouldDirty: true }
                            );
                          }}
                        />
                      );
                    })
                  : null}
              </Stack>
            ) : null}
          </Stack>
        </Stack>
      ) : null}
      <Stack direction="row" gap={1}>
        <Button
          size="small"
          onClick={() =>
            setShowAdditionalConfigurationOptions(
              !showAdditonalConfigurationOptions
            )
          }
          sx={{ width: 'fit-content' }}
        >
          {showAdditonalConfigurationOptions ? 'Hide CC/Bcc' : 'Show CC/Bcc'}
        </Button>
      </Stack>

      <Typography sx={{ fontWeight: 'bold' }}>Subject</Typography>
      <Box sx={{ height: 110 }}>
        <MergeTagOnlyEditor
          editorRef={editorSubjectRef}
          content={emailSubject}
          setContent={setSubject}
          placeholder="Type your email subject here..."
          mergeTags={[generalFieldMergeTags]}
        />
      </Box>
      <Typography sx={{ fontWeight: 'bold' }}>Body</Typography>
      <Box sx={{ height: 200 }}>
        <DistractionFreeEditor
          editorRef={editorRef}
          content={htmlBody}
          setContent={setHtmlBody}
          placeholder="Type your email body here..."
          mergeTags={[generalFieldMergeTags]}
        />
      </Box>
    </Stack>
  );
}
